// API base URL - empty in dev (proxy), set to Function App URL in production
const BASE_URL = import.meta.env.VITE_API_URL || '';

/** Token storage – set by AuthContext after Google login */
let _authToken: string | null = null;

/** Callback for handling 401 unauthorized responses */
let _onUnauthorized: (() => void) | null = null;

export function setAuthToken(token: string | null) {
  _authToken = token;
}

export function getAuthToken(): string | null {
  return _authToken;
}

export function setOnUnauthorized(callback: (() => void) | null) {
  _onUnauthorized = callback;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Add Google ID token as Bearer token
  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  // Only set Content-Type for non-FormData requests
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 unauthorized - token expired or invalid
    if (response.status === 401 && _onUnauthorized) {
      _onUnauthorized();
    }
    
    const errorBody = await response.json().catch(() => ({
      error: { code: 'unknown', message: response.statusText },
    }));
    throw new Error(errorBody.error?.message || response.statusText);
  }

  return response.json();
}

export const api = {
  get: <T = any>(path: string) => request<T>(path),

  post: <T = any>(path: string, body?: any) =>
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: <T = any>(path: string, body: any) =>
    request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T = any>(path: string, body: any) =>
    request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T = any>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  upload: <T = any>(path: string, formData: FormData) =>
    request<T>(path, {
      method: 'POST',
      body: formData,
    }),
};
